import React, { FC, useState } from 'react'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useUserContext } from 'apps/fleet-planner/context'
import { useDedicatedLanesUpdateSMS } from 'apps/fleet-planner/types/apiTypes'
import { Button } from 'transfix-ui'
import formatPhone from 'utils/formatPhone'
import { SubscriptionModal } from './SubscriptionModal'

interface ISubscriptionContainerProps {
  dedicatedLaneId: number
  smsNotificationsEnabled: boolean
  updateDedicatedLanes: (id: number, smsEnabled: boolean, newLaneId: string) => void
}

const useSubscriptionContainerStyles = makeStyles(() => ({
  subscribeText: {
    paddingBottom: 0,
    color: brandColors.coolGray5,
  },
  subscribeContainer: {
    width: '100%',
    gap: '5px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}))

export const SubscriptionContainer: FC<ISubscriptionContainerProps> = ({
  dedicatedLaneId,
  smsNotificationsEnabled,
  updateDedicatedLanes,
}) => {
  const classes = useSubscriptionContainerStyles()
  const [isSubscriptionMobileModalOpen, setIsSubscriptionMobileModalOpen] = useState(false)
  const { userData } = useUserContext()
  const { user } = userData || {}
  const { cellPhone } = user || {}

  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const { mutate: dedicatedLanesUpdateSMS } = useDedicatedLanesUpdateSMS({
    id: dedicatedLaneId,
  })

  const closeSubscriptionHandler = () => {
    setIsSubscriptionMobileModalOpen(false)
  }

  const subscribeForLane = async () => {
    try {
      const result = await dedicatedLanesUpdateSMS({
        lane: {
          smsNotificationsEnabled: true,
        },
      })

      closeSubscriptionHandler()

      const newLaneId = result.laneId
      updateDedicatedLanes(dedicatedLaneId, true, newLaneId)
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: 'Something went wrong. Please contact us.',
      })
    }
  }

  const openSubscriptionHandler = () => {
    setIsSubscriptionMobileModalOpen(false)

    if (!smsNotificationsEnabled) {
      subscribeForLane()
    }
  }

  const onUnsubscribe = async () => {
    try {
      const result = await dedicatedLanesUpdateSMS({
        lane: {
          smsNotificationsEnabled: false,
        },
      })

      const newLaneId = result.laneId
      updateDedicatedLanes(dedicatedLaneId, false, newLaneId)
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: 'Something went wrong. Please contact us.',
      })
    }
  }

  const clickSubscriptionHandler = () => {
    if (!cellPhone || smsNotificationsEnabled) {
      setIsSubscriptionMobileModalOpen(true)
    } else {
      subscribeForLane()
    }
  }

  return (
    <>
      <Box pb={0.5}>
        <Box>
          <Typography className={classes.subscribeText} variant='body2'>
            {smsNotificationsEnabled
              ? `You're receiving text updates to ${formatPhone(cellPhone)}`
              : "You're not subscribed for text message updates"}
          </Typography>
          <Box className={classes.subscribeContainer}>
            <Button noPadding type='button' variant='text' onClick={clickSubscriptionHandler}>
              {smsNotificationsEnabled ? 'Update Number' : 'Subscribe'}
            </Button>

            {smsNotificationsEnabled && (
              <>
                <Typography> | </Typography>
                <div>
                  <Button noPadding type='button' variant='text' onClick={onUnsubscribe}>
                    Unsubscribe
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Box>

        <SubscriptionModal
          openToast={openToast}
          onSubmit={openSubscriptionHandler}
          isLoading={false}
          isOpen={isSubscriptionMobileModalOpen}
          onClose={closeSubscriptionHandler}
        />
      </Box>

      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </>
  )
}
